import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase'; 
import { Player } from '../types/Player';
import { PlayerEvaluation } from '../types/playerEvaluation';

// Canonical list of valid team levels
const TEAM_LEVELS = ['Rec', 'Alliance', 'Elite', 'Premier'] as const;
type TeamLevel = typeof TEAM_LEVELS[number]; // Type for valid levels

// Helper to convert team_recommendations array from DB (string[]) to object for form state
export function teamRecommendationsArrayToObj(arr?: string[] | null): { [key in TeamLevel]?: boolean } {
  const obj: { [key in TeamLevel]?: boolean } = {};
  if (Array.isArray(arr)) {
    arr.forEach((level: string) => {
      // Ensure level is a valid TeamLevel before adding
      if (TEAM_LEVELS.includes(level as TeamLevel)) {
        obj[level as TeamLevel] = true;
      }
    });
  }
  return obj;
}

// Helper to convert form state object { [key: string]: boolean } back to string[] for DB
export function teamRecommendationsObjToArray(obj?: { [key in TeamLevel]?: boolean } | null): string[] {
  if (!obj) return [];
  // Filter TEAM_LEVELS based on truthiness in the object
  return TEAM_LEVELS.filter(level => obj[level]);
}


/**
 * Custom hook to manage the state and interactions for the currently selected player's evaluation form
 * @param selectedPlayer The currently selected player
 * @param evaluationSessionId The ID of the evaluation session
 * @param coachId The ID of the current coach
 * @param onEvaluationUpdate Callback function to be called after a successful save (passes the saved evaluation)
 * @param allPlayerEvaluations All evaluations fetched for the selected player by the parent component
 * @param isLoadingAllEvaluations Loading state for allPlayerEvaluations
 * @returns Object containing evaluation state and handlers
 */
export function usePlayerEvaluationForm(
  selectedPlayer: Player | null,
  evaluationSessionId: string | undefined,
  coachId: string,
  onEvaluationUpdate: (updatedEvaluation: PlayerEvaluation) => void,
  allPlayerEvaluations: PlayerEvaluation[] | null, // Added: All evaluations for the player
  isLoadingAllEvaluations: boolean                 // Added: Loading state
) {
  // State for the form data being actively edited
  const [formData, setFormData] = useState<Partial<Omit<PlayerEvaluation, 'team_recommendations'> & { team_recommendations?: { [key in TeamLevel]?: boolean } }>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null); // New state for error message
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialFormDataRef = useRef<Partial<Omit<PlayerEvaluation, 'team_recommendations'> & { team_recommendations?: { [key in TeamLevel]?: boolean } }>>({});
  const isDirtyRef = useRef(isDirty);
  const saveEvaluationRef = useRef<() => Promise<void>>();
  const isCleanupSaveRef = useRef(false);

  // Helper to define the empty state for the form
  const getEmptyFormData = useCallback((): Partial<Omit<PlayerEvaluation, 'team_recommendations'> & { team_recommendations?: { [key in TeamLevel]?: boolean } }> => ({
    coach_id: coachId,
    evaluation_session_id: evaluationSessionId || undefined,
    ratings: {},
    notes: '',
    team_recommendations: {} // Store as object internally
  }), [coachId, evaluationSessionId]);

  // --- Keep refs updated ---
  useEffect(() => {
    initialFormDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Effect to FIND evaluation in passed data OR load empty form
  useEffect(() => {
    // If evaluations are still loading, wait.
    if (isLoadingAllEvaluations) {
        return;
    }

    // Reset error when dependencies change (player, session, etc.)
    setSaveError(null);

    // If no player/session/coach, clear the form
    if (!selectedPlayer || !evaluationSessionId || !coachId) {
        setFormData({});
        setIsDirty(false);
        setSaveStatus("idle");
        initialFormDataRef.current = {};
        return;
    }

    // Find the current coach's evaluation in the passed-in list
    const myEval = allPlayerEvaluations?.find(
        (ev) => ev.coach_id === coachId && ev.evaluation_session_id === evaluationSessionId
    ) || null;

    if (myEval) {
        console.log(`[usePlayerEvaluationForm effect] Found existing evaluation for coach ${coachId}, player ${selectedPlayer.id} in passed data.`);
        const mappedData = {
          ...myEval,
          team_recommendations: teamRecommendationsArrayToObj(myEval.team_recommendations as any)
        };
        if ('evaluation_id' in mappedData && mappedData.evaluation_id !== mappedData.evaluation_session_id) {
            delete mappedData.evaluation_id;
        }

        setFormData(mappedData);
        initialFormDataRef.current = { ...mappedData };
        setIsDirty(false);
        setSaveStatus("idle");
    } else {
        console.log(`[usePlayerEvaluationForm effect] No existing evaluation found for coach ${coachId}, player ${selectedPlayer.id}. Initializing empty form.`);
        const emptyData = getEmptyFormData();
        setFormData(emptyData);
        initialFormDataRef.current = { ...emptyData };
        setIsDirty(false);
        setSaveStatus("idle");
    }

    // Cleanup function for component unmount or player change
    return () => {
      if (isDirtyRef.current && saveEvaluationRef.current) {
        console.log('[Player Change Cleanup / Effect Deps Change] Form was dirty, triggering immediate save.');
        isCleanupSaveRef.current = true;
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
        saveEvaluationRef.current();
      } else {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
      }
    };
  }, [
      selectedPlayer,
      evaluationSessionId,
      coachId,
      allPlayerEvaluations,
      isLoadingAllEvaluations,
      getEmptyFormData
    ]);


  // --- Handlers to update local formData state ---

  const handleScoreChange = useCallback((category: string, value: number) => {
    setFormData((prevData) => {
      const currentData = Object.keys(prevData).length === 0 ? getEmptyFormData() : prevData;
      const newRatings = { ...(currentData.ratings || {}), [category]: value };
      return { ...currentData, ratings: newRatings };
    });
    setIsDirty(true);
    setSaveStatus("idle");
    setSaveError(null); // Clear error on interaction
  }, [getEmptyFormData]);

  const handleNotesChange = useCallback((notes: string) => {
    setFormData((prevData) => {
      const currentData = Object.keys(prevData).length === 0 ? getEmptyFormData() : prevData;
      return { ...currentData, notes: notes };
    });
    setIsDirty(true);
    setSaveStatus("idle");
    setSaveError(null); // Clear error on interaction
  }, [getEmptyFormData]);

  const handleTeamRecommendationChange = useCallback((teamLevel: TeamLevel, selected: boolean) => {
    setFormData((prevData) => {
      const currentData = Object.keys(prevData).length === 0 ? getEmptyFormData() : prevData;
      const currentRecommendations = currentData.team_recommendations || {};
      const newRecommendations = { ...currentRecommendations, [teamLevel]: selected };
      if (!selected) {
        delete newRecommendations[teamLevel];
      }
      return { ...currentData, team_recommendations: newRecommendations };
    });
    setIsDirty(true);
    setSaveStatus("idle");
    setSaveError(null); // Clear error on interaction
  }, [getEmptyFormData]);

  // Reset form to initial state
  // Not currently used, but kept for potential future use
  const resetForm = useCallback(() => {
    const initialData = initialFormDataRef.current;
    setFormData(initialData);
    setIsDirty(false);
    setSaveStatus("idle");
    setSaveError(null); // Clear error on reset
  }, []);

  // Save Evaluation Logic (Upsert)
  const saveEvaluation = useCallback(async () => {
    const currentIsDirty = isDirtyRef.current;
    const currentFormData = initialFormDataRef.current;

    if (!currentIsDirty || !selectedPlayer || !currentFormData || !evaluationSessionId || !coachId || Object.keys(currentFormData).length === 0) {
      console.log('[saveEvaluation] Conditions not met, aborting save.');
      if (isCleanupSaveRef.current) isCleanupSaveRef.current = false;
      return;
    }

    setSaveStatus("saving");
    setSaveError(null); // Clear previous errors before attempting to save

    try {
      const dataToSave: Omit<PlayerEvaluation, 'created_at' | 'updated_at'> & { id?: string } = {
        player_id: selectedPlayer.id,
        evaluation_id: evaluationSessionId,
        coach_id: coachId,
        ratings: currentFormData.ratings || {},
        notes: currentFormData.notes || "",
        team_recommendations: teamRecommendationsObjToArray(currentFormData.team_recommendations),
        ...(currentFormData.id && { id: currentFormData.id }),
        evaluation_session_id: evaluationSessionId,
      };
       if (dataToSave.evaluation_id) {
        delete dataToSave.evaluation_session_id;
      }

      const { error, data } = await supabase
        .from('player_evaluations')
        .upsert(dataToSave, { onConflict: 'player_id,evaluation_id,coach_id' })
        .select()
        .single();

      if (error) {
        console.error("Error saving evaluation:", error);
        setSaveStatus("error");
        setSaveError(error.message); // Set the error message state
        if (isCleanupSaveRef.current) isCleanupSaveRef.current = false;
        return;
      }

      if (data) {
        const mappedSavedData: PlayerEvaluation = {
          ...data,
          evaluation_session_id: data.evaluation_id || data.evaluation_session_id,
        };
        if ('evaluation_id' in mappedSavedData && mappedSavedData.evaluation_id !== mappedSavedData.evaluation_session_id) {
            delete mappedSavedData.evaluation_id;
        }

        onEvaluationUpdate(mappedSavedData);

        const cleanupInProgress = isCleanupSaveRef.current;
        if (cleanupInProgress) {
          console.log('[saveEvaluation] Cleanup save finished.');
          isCleanupSaveRef.current = false;
          // Keep form state, dirty status, etc. as they were. Main effect will handle reset for new player.
        } else {
          console.log('[saveEvaluation] Normal save finished.');
          const formStateData = {
              ...mappedSavedData,
              team_recommendations: teamRecommendationsArrayToObj(mappedSavedData.team_recommendations)
          };
          setSaveStatus("saved");
          setIsDirty(false);
          // setFormData(formStateData); // Removed this line because it was causing issues with the form state
          initialFormDataRef.current = { ...formStateData };
        }
      } else {
         console.error("Upsert operation returned no data, but also no error.");
         setSaveStatus("error");
         setSaveError("Save operation completed but returned no data.");
         if (isCleanupSaveRef.current) isCleanupSaveRef.current = false;
      }
    } catch (err: any) {
      console.error("Exception during saveEvaluation execution:", err);
      setSaveStatus("error");
      setSaveError(err.message || "An unexpected error occurred during save."); // Set error message
      if (isCleanupSaveRef.current) isCleanupSaveRef.current = false;
    }
  }, [selectedPlayer, evaluationSessionId, coachId, onEvaluationUpdate]);

  // Keep the latest saveEvaluation function in a ref so it can be used reliably (e.g., in debounced saves or cleanup)
  useEffect(() => {
    saveEvaluationRef.current = saveEvaluation;
  }, [saveEvaluation]);

  // Debounced auto-save: When form is dirty and contains coach_id, wait 2 seconds after last change, then trigger save.
  useEffect(() => {
    if (isDirty && formData && Object.keys(formData).length > 0 && formData.coach_id) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      console.log('[debounced useEffect] Form is dirty, setting save timeout...');
      saveTimeoutRef.current = setTimeout(() => {
        console.log('[debounced useEffect] Debounced save triggered');
        if (saveEvaluationRef.current) {
          saveEvaluationRef.current();
        }
      }, 2000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [isDirty, formData]);

  return {
    myEvaluation: formData,
    handleScoreChange,
    handleNotesChange,
    handleTeamRecommendationChange,
    saveEvaluation: saveEvaluationRef.current || (async () => {}),
    resetForm,
    saveStatus,
    isDirty,
    saveError, // Return the error message state
  };
}